import React, { useState, useEffect, type ChangeEvent, useMemo } from 'react';
import productService from '../services/productService';
import { useAppSelector } from '../store/hooks';
import ProductForm from '../components/ProductForm';
import type { Product } from '../types';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../utils/i18nFormat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faPlusCircle, faSearch, faEdit, faTrash, faChevronLeft, faChevronRight, faFileInvoice, faArrowUp, faArrowDown, faChartLine } from '@fortawesome/free-solid-svg-icons';
import { uploadImageToGcs } from '../services/uploadService';
import { productsFeatures } from '../config/productsFeatures';
import { createDerivedCategoriesSource } from '../services/categorySource';
import { useNotifications } from '../components/ui/notifications';
import { useDeleteConfirmation } from '../components/ui/DeleteConfirmationProvider';

type FormProduct = {
  nombre: string;
  descripcion: string;
  precio: string;
  sku: string;
  stock: string;
  categoria: string;
  es_visible: boolean;
  es_comprable: boolean;
  imagen_url: string;
};

function ProductsPage() {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const confirmDelete = useDeleteConfirmation();
  const user = useAppSelector((s) => s.auth.user);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'list' | 'create'>('list');
  const [showEditForm, setShowEditForm] = useState<boolean>(false);
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [formProduct, setFormProduct] = useState<FormProduct>({
    nombre: '',
    descripcion: '',
    precio: '',
    sku: '',
    stock: '',
    categoria: '',
    es_visible: true,
    es_comprable: true,
    imagen_url: '',
  });

  // Categories extra (UI-only additions, before any product uses them)
  const [categoriesExtra, setCategoriesExtra] = useState<string[]>([]);
  // Non-destructive search and category filter
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Derive unique categories from products
  const derivedCategories: string[] = useMemo(() => {
    const set = new Set<string>();
    for (const p of products || []) {
      const c = String(p?.categoria ?? '').trim();
      if (c) set.add(c);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [products]);

  // Options shown in selectors/filters = derived ∪ extras
  const categoryOptions: string[] = useMemo(() => {
    const set = new Set<string>(derivedCategories);
    for (const e of categoriesExtra) {
      const n = String(e || '').trim();
      if (n) set.add(n);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [derivedCategories, categoriesExtra]);

  // Create a derived CategorySource to support inline create/rename across loaded products
  const categorySource = useMemo(() => {
    return createDerivedCategoriesSource({
      getProducts: () => products,
      setProducts: (updater) => setProducts((prev) => updater(prev)),
      getExtras: () => categoriesExtra,
      setExtras: (updater) => setCategoriesExtra((prev) => updater(prev)),
    });
  }, [products, categoriesExtra]);

  // Pagination and Sorting states
  const [page, setPage] = useState(1);
  const limit = 15;
  const [sortBy, setSortBy] = useState('nombre');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setOrder('asc');
    }
    setPage(1);
  };

  // List used for rendering table (non-destructive filtering & sorting)
  const filteredProducts: Product[] = useMemo(() => {
    let list = products.slice();
    const q = (searchQuery || '').trim().toLowerCase();
    if (q) {
      list = list.filter(p =>
        String(p.nombre || '').toLowerCase().includes(q) ||
        String(p.sku || '').toLowerCase().includes(q) ||
        String(p.categoria || '').toLowerCase().includes(q)
      );
    }
    const cat = (selectedCategory || '').trim();
    if (cat) {
      list = list.filter(p => String(p.categoria || '') === cat);
    }

    // Apply sorting
    list.sort((a: any, b: any) => {
      let valA = a[sortBy];
      let valB = b[sortBy];

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA === undefined || valA === null) valA = '';
      if (valB === undefined || valB === null) valB = '';

      if (valA < valB) return order === 'asc' ? -1 : 1;
      if (valA > valB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [products, searchQuery, selectedCategory, sortBy, order]);

  const total = filteredProducts.length;
  const pages = Math.ceil(total / limit) || 1;
  const paginatedProducts = filteredProducts.slice((page - 1) * limit, page * limit);

  // Función para cargar los productos
  const fetchProducts = async () => {
    if (!user || !user.comercioKey) {
      setLoading(false);
      setError(t('productsPage.errors.noCommerceKey'));
      return;
    }
    try {
      const data: Product[] = await productService.getProducts(user.comercioKey);
      setProducts(data);
    } catch (err: any) {
      setError(err?.response?.data?.message || t('productsPage.errors.load'));
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  // Cargar productos al inicio o cuando el usuario cambie
  useEffect(() => {
    fetchProducts();
  }, [user]);

  // Manejar cambios en los inputs del formulario (para creación y edición en línea)
  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, productId: number | null = null) => {
    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    const name = (target as any).name as string;
    const isCheckbox = (target as HTMLInputElement).type === 'checkbox';
    const newValue: any = isCheckbox ? (target as HTMLInputElement).checked : target.value;

    if (productId != null) {
      // Edición en línea: convertir tipos numéricos cuando aplique
      setProducts(products.map((p) =>
        p.id === productId
          ? ({ ...p, [name]: (name === 'precio' || name === 'stock') ? Number(newValue) : newValue } as Product)
          : p
      ));
    } else {
      // Formulario de creación
      setFormProduct((prev) => ({
        ...prev,
        [name]: newValue,
      }));
    }
  };

  // Helper para establecer un campo del formulario de creación/edición (p.ej., tras subir a GCS)
  const setFormField = (name: string, value: any) => {
    setFormProduct((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Subir imagen a GCS bajo el prefijo 'products' usando la carpeta comercio_key
  const handleUploadImage = async (file: File): Promise<string> => {
    if (!user || !user.comercioKey) {
      throw new Error(t('productsPage.errors.noCommerceKey') as string || 'No commerce key');
    }
    const url = await uploadImageToGcs(user.comercioKey, file, 'products');
    // Establecer también en el estado del formulario inmediatamente (además de setField desde ProductForm)
    setFormProduct((prev) => ({ ...prev, imagen_url: url }));
    return url;
  };

  // Manejar el envío del formulario (crear o actualizar)
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!user || !user.comercioKey) {
      setError(t('productsPage.errors.noCommerceKey'));
      return;
    }

    try {
      const productData = {
        ...formProduct,
        precio: Number(formProduct.precio),
        stock: Number(formProduct.stock),
      };

      const createdProduct: Product = await productService.createProduct(user.comercioKey, productData);
      setProducts([...products, createdProduct]);
      notifications.success('Producto creado', `${createdProduct.nombre || productData.nombre} se añadió correctamente.`);
      // Limpiar formulario y ocultarlo
      setFormProduct({
        nombre: '',
        descripcion: '',
        precio: '',
        sku: '',
        stock: '',
        categoria: '',
        es_visible: true,
        es_comprable: true,
        imagen_url: '',
      });
      setActiveTab('list');
    } catch (err: any) {
      const message = err?.response?.data?.message || t('productsPage.errors.create');
      setError(message);
      notifications.error('No se pudo crear el producto', typeof message === 'string' ? message : undefined);
      console.error('Error creating product:', err);
    }
  };

  // Handle update from edit form (modal)
  const handleUpdateSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (!user || !user.comercioKey || editingProductId == null) {
      setError(t('productsPage.errors.noCommerceKey'));
      return;
    }
    try {
      const productData = {
        ...formProduct,
        precio: Number(formProduct.precio),
        stock: Number(formProduct.stock),
      };
      const updatedProduct: Product = await productService.updateProduct(user.comercioKey, editingProductId, productData);
      setProducts(products.map((p) => (p.id === updatedProduct.id ? updatedProduct : p)));
      setShowEditForm(false);
      setEditingProductId(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || t('productsPage.errors.update'));
      console.error('Error updating product:', err);
    }
  };

  // Manejar clic en el botón de editar (para activar edición en línea)
  const handleEditClick = (product: Product) => {
    setEditingProductId(product.id);
    setActiveTab('list');
    setShowEditForm(true);
    // Inicializar datos del formulario de edición con strings donde corresponda
    setFormProduct({
      nombre: product.nombre || '',
      descripcion: product.descripcion || '',
      precio: String(product.precio ?? ''),
      sku: String(product.sku ?? ''),
      stock: String(product.stock ?? ''),
      categoria: String(product.categoria ?? ''),
      es_visible: !!product.es_visible,
      es_comprable: !!product.es_comprable,
      imagen_url: String(product.imagen_url ?? ''),
    });
  };

  // Manejar clic en el botón de eliminar
  const handleDeleteClick = async (productId: number) => {
    if (!user || !user.comercioKey) {
      setError(t('productsPage.errors.noCommerceKey'));
      return;
    }
    if (await confirmDelete({
      title: t('productsPage.item.buttons.delete', 'Eliminar producto'),
      message: t('productsPage.confirmDelete'),
    })) {
      try {
        await productService.deleteProduct(user.comercioKey, productId);
        setProducts(products.filter((p) => p.id !== productId));
      } catch (err: any) {
        setError(err?.response?.data?.message || t('productsPage.errors.delete'));
        console.error('Error deleting product:', err);
      }
    }
  };

  // Renderizado condicional para estados de carga y error
  if (loading) return <p className="text-center text-white/60 mt-8">{t('productsPage.loading')}</p>;
  if (error) return <p className="text-center text-red-600 mt-8">{t('productsPage.errorPrefix')}{error}</p>;
  if (!user || !user.comercioKey) return <p className="text-center text-white/60 mt-8">{t('productsPage.needLogin')}</p>;

  if (showEditForm) {
    const totalInventoryValue = Number(formProduct.precio) * Number(formProduct.stock) || 0;

    return (
      <div className="max-w-7xl mx-auto space-y-6 animate-fade-in-down">
        {/* Header / Back Button */}
        <div className="flex items-center space-x-4 mb-6">
          <button
            onClick={() => { setShowEditForm(false); setEditingProductId(null); fetchProducts(); }}
            className="btn-taratic btn-ghost-dark px-4 py-2 flex items-center gap-2 rounded-xl"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            Volver
          </button>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <FontAwesomeIcon icon={faBoxOpen} className="mr-3 text-blue-400" />
            Editar Producto: <span className="text-white/60 ml-2">{formProduct.nombre}</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column: Product Form */}
          <div className="lg:col-span-5 bg-black/30 backdrop-blur-md p-6 rounded-3xl border border-white/20 shadow-xl">
            <h3 className="text-xl font-semibold text-white mb-6 border-b border-white/10 pb-4">Detalles del Producto</h3>
            <ProductForm
              formProduct={formProduct}
              handleInputChange={handleInputChange}
              handleSubmit={handleUpdateSubmit}
              isEditing={true}
              handleCancelEdit={() => { setShowEditForm(false); setEditingProductId(null); fetchProducts(); }}
              setField={setFormField}
              onUploadImage={handleUploadImage}
              categoriesEnabled={productsFeatures.categories.enabled}
              categoryOptions={categoryOptions}
              onCreateCategory={async (name: string) => {
                try {
                  const created = await categorySource.create(name);
                  setFormField('categoria', created);
                } catch { }
              }}
              onRenameCategory={async (oldName: string, newName: string) => {
                try {
                  await categorySource.rename(oldName, newName);
                  if (String(formProduct.categoria || '').trim().toLowerCase() === String(oldName || '').trim().toLowerCase()) {
                    setFormField('categoria', newName);
                  }
                } catch { }
              }}
            />
          </div>

          {/* Right Column: Analysis */}
          <div className="lg:col-span-7 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="bg-black/30 backdrop-blur-md p-6 rounded-3xl border border-white/20 shadow-xl flex flex-col justify-center">
                <p className="text-xs font-bold text-white/50 uppercase tracking-wider mb-2">VALOR EN INVENTARIO</p>
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faChartLine} className="text-2xl text-green-400" />
                  <span className="text-4xl font-bold text-white">{formatCurrency(totalInventoryValue)}</span>
                </div>
              </div>
              <div className="bg-black/30 backdrop-blur-md p-6 rounded-3xl border border-white/20 shadow-xl flex flex-col justify-center">
                <p className="text-xs font-bold text-white/50 uppercase tracking-wider mb-2">UNIDADES DISPONIBLES</p>
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faBoxOpen} className="text-2xl text-blue-400" />
                  <span className="text-4xl font-bold text-white">{formProduct.stock || 0} u.</span>
                </div>
              </div>
            </div>

            {/* Simulated Table */}
            <div className="bg-black/30 backdrop-blur-md rounded-3xl border border-white/20 shadow-xl overflow-hidden">
              <div className="px-6 py-5 border-b border-white/10 flex items-center">
                <FontAwesomeIcon icon={faFileInvoice} className="text-white/50 mr-3 text-lg" />
                <h3 className="text-lg font-semibold text-white">Historial de Movimientos</h3>
              </div>
              <div className="p-6 flex flex-col items-center justify-center text-center text-white/50 h-64">
                <FontAwesomeIcon icon={faChartLine} className="text-5xl mb-4 opacity-50" />
                <p>Aún no hay datos de ventas registrados para este producto.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto dark-bg overflow-x-hidden">

      {/* Formulario de Creación de Producto (colapsable) */}
      {(activeTab === 'create') && (
        <div className="mb-8 animate-fade-in-down">
          <ProductForm
            formProduct={formProduct}
            handleInputChange={handleInputChange}
            handleSubmit={handleSubmit}
            isEditing={false} // Siempre false para el formulario de creación
            handleCancelEdit={() => { }} // No se usa en el formulario de creación
            setField={setFormField}
            onUploadImage={handleUploadImage}
            categoriesEnabled={productsFeatures.categories.enabled}
            categoryOptions={categoryOptions}
            onCreateCategory={async (name: string) => {
              try {
                const created = await categorySource.create(name);
                setFormField('categoria', created);
              } catch { }
            }}
            onRenameCategory={async (oldName: string, newName: string) => {
              try {
                await categorySource.rename(oldName, newName);
                if (String(formProduct.categoria || '').trim().toLowerCase() === String(oldName || '').trim().toLowerCase()) {
                  setFormField('categoria', newName);
                }
              } catch { }
            }}
          />
        </div>
      )}

      {/* Edit Form moved to full page conditionally above */}

      {/* --- FILTERS & SEARCH BAR --- */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 space-y-4 md:space-y-0">
        <div className="flex items-center space-x-4 w-full md:w-auto">
          {/* Header/Title + Actions */}
          <button
            onClick={() => { setActiveTab(activeTab === 'list' ? 'create' : 'list'); }}
            className={`btn-taratic ${activeTab === 'create' ? 'btn-ghost-dark' : 'btn-blue'} px-5 py-2 !text-sm whitespace-nowrap`}
          >
            <FontAwesomeIcon icon={activeTab === 'create' ? faBoxOpen : faPlusCircle} className="mr-2" />
            {(activeTab === 'create') ? t('productsPage.toggleHideForm') : t('productsPage.toggleShowForm')}
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); setPage(1); }} className="relative w-full md:w-1/3">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-white/70" />
          </div>
          <input
            type="text"
            placeholder={t('productsPage.searchPlaceholder') || 'Buscar...'}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="w-full bg-black/20 border border-white/20 text-white text-sm rounded-full focus:ring-blue-400 focus:border-blue-400 block pl-11 p-2.5 placeholder-white/60 shadow-inner transition-all duration-300 hover:bg-black hover:shadow-inner-glow hover:shadow-inner-glow-white-2xl focus:bg-black focus:shadow-inner-glow focus:shadow-inner-glow-white-2xl"
          />
        </form>
      </div>

      {productsFeatures.categories.enabled && (
        <div className="flex overflow-x-auto gemini-scrollbar space-x-2 mb-6 pb-2">
          {['all', ...categoryOptions].map((s) => {
            const isActive = s === 'all' ? selectedCategory === '' : selectedCategory === s;
            let activeClasses = '';

            if (isActive) {
              if (s === 'all') activeClasses = '!bg-blue-500/20 text-blue-300 border-blue-500/30';
              else activeClasses = '!bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
            }

            return (
              <button
                key={s}
                onClick={() => {
                  setSelectedCategory(s === 'all' ? '' : s);
                  setPage(1);
                }}
                className={`btn-taratic px-5 py-2 !text-sm border ${isActive ? activeClasses : 'btn-ghost-dark !shadow-none hover:shadow-inner-glow hover:shadow-inner-glow-white-2xl hover:text-shadow-glow-white hover:scale-100'} whitespace-nowrap`}
              >
                {s === 'all' ? 'Todo' : s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            );
          })}
        </div>
      )}

      {/* --- DATA TABLE --- */}
      <div className="bg-black/30 backdrop-blur-md shadow-xl rounded-3xl overflow-hidden border border-white/20 mb-10">
        {filteredProducts.length === 0 ? (
          <div className="p-16 text-center text-white flex flex-col items-center">
            <FontAwesomeIcon icon={faBoxOpen} className="text-4xl mb-4 text-white/50 drop-shadow-md" />
            <p className="text-lg font-medium">{t('productsPage.empty')}</p>
            <p className="text-sm mt-1 text-white/70">Intenta cambiar los filtros o crear un nuevo producto.</p>
          </div>
        ) : (
          <div className="overflow-x-auto gemini-scrollbar">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="bg-black/75 border-b border-white/20">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-black/20 transition-colors group" onClick={() => handleSort('imagen_url')}>
                    <div className="flex items-center justify-start whitespace-nowrap">
                      {t('productsPage.table.headers.image')}
                      <FontAwesomeIcon icon={sortBy === 'imagen_url' && order === 'desc' ? faArrowDown : faArrowUp} className={`ml-2 transition-opacity duration-200 ${sortBy === 'imagen_url' ? 'opacity-100 text-white/70' : 'opacity-0 group-hover:opacity-30'}`} />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-black/20 transition-colors group" onClick={() => handleSort('nombre')}>
                    <div className="flex items-center justify-start whitespace-nowrap">
                      {t('productsPage.table.headers.nameDescription')}
                      <FontAwesomeIcon icon={sortBy === 'nombre' && order === 'desc' ? faArrowDown : faArrowUp} className={`ml-2 transition-opacity duration-200 ${sortBy === 'nombre' ? 'opacity-100 text-white/70' : 'opacity-0 group-hover:opacity-30'}`} />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-black/20 transition-colors group" onClick={() => handleSort('sku')}>
                    <div className="flex items-center justify-start whitespace-nowrap">
                      {t('productsPage.table.headers.sku')}
                      <FontAwesomeIcon icon={sortBy === 'sku' && order === 'desc' ? faArrowDown : faArrowUp} className={`ml-2 transition-opacity duration-200 ${sortBy === 'sku' ? 'opacity-100 text-white/70' : 'opacity-0 group-hover:opacity-30'}`} />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-black/20 transition-colors group" onClick={() => handleSort('categoria')}>
                    <div className="flex items-center justify-start whitespace-nowrap">
                      {t('productsPage.table.headers.category')}
                      <FontAwesomeIcon icon={sortBy === 'categoria' && order === 'desc' ? faArrowDown : faArrowUp} className={`ml-2 transition-opacity duration-200 ${sortBy === 'categoria' ? 'opacity-100 text-white/70' : 'opacity-0 group-hover:opacity-30'}`} />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-black/20 transition-colors group" onClick={() => handleSort('precio')}>
                    <div className="flex items-center justify-end whitespace-nowrap">
                      {t('productsPage.table.headers.price')}
                      <FontAwesomeIcon icon={sortBy === 'precio' && order === 'desc' ? faArrowDown : faArrowUp} className={`ml-2 transition-opacity duration-200 ${sortBy === 'precio' ? 'opacity-100 text-white/70' : 'opacity-0 group-hover:opacity-30'}`} />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-black/20 transition-colors group" onClick={() => handleSort('stock')}>
                    <div className="flex items-center justify-end whitespace-nowrap">
                      {t('productsPage.table.headers.stock')}
                      <FontAwesomeIcon icon={sortBy === 'stock' && order === 'desc' ? faArrowDown : faArrowUp} className={`ml-2 transition-opacity duration-200 ${sortBy === 'stock' ? 'opacity-100 text-white/70' : 'opacity-0 group-hover:opacity-30'}`} />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-black/20 transition-colors group" onClick={() => handleSort('es_visible')}>
                    <div className="flex items-center justify-center whitespace-nowrap">
                      {t('productsPage.table.headers.visible')}
                      <FontAwesomeIcon icon={sortBy === 'es_visible' && order === 'desc' ? faArrowDown : faArrowUp} className={`ml-2 transition-opacity duration-200 ${sortBy === 'es_visible' ? 'opacity-100 text-white/70' : 'opacity-0 group-hover:opacity-30'}`} />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider cursor-pointer hover:bg-black/20 transition-colors group" onClick={() => handleSort('es_comprable')}>
                    <div className="flex items-center justify-center whitespace-nowrap">
                      {t('productsPage.table.headers.purchasable')}
                      <FontAwesomeIcon icon={sortBy === 'es_comprable' && order === 'desc' ? faArrowDown : faArrowUp} className={`ml-2 transition-opacity duration-200 ${sortBy === 'es_comprable' ? 'opacity-100 text-white/70' : 'opacity-0 group-hover:opacity-30'}`} />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-bold text-white uppercase tracking-wider">{t('productsPage.table.headers.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {paginatedProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-black cursor-pointer transition-colors duration-200 group">
                    <td className="px-4 py-4 whitespace-nowrap align-middle">
                      {product.imagen_url ? (
                        <img src={product.imagen_url} alt={product.nombre} className="h-16 w-16 object-contain rounded-md border border-white/20" />
                      ) : (
                        <div className="h-16 w-16 flex items-center justify-center bg-black/20 rounded-md text-xs text-white/50 border border-white/10">No img</div>
                      )}
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="text-sm font-semibold text-white">{product.nombre}</div>
                      <div className="text-xs text-white/60 line-clamp-2 max-w-xs">{product.descripcion}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap align-top text-sm text-white/80">{product.sku ?? '—'}</td>
                    <td className="px-4 py-4 whitespace-nowrap align-top text-sm text-white/80">{product.categoria ?? '—'}</td>
                    <td className="px-4 py-4 whitespace-nowrap align-top text-right text-sm font-bold text-green-400">{formatCurrency(Number(product.precio))}</td>
                    <td className="px-4 py-4 whitespace-nowrap align-top text-right text-sm text-white/80">{product.stock ?? 0}</td>
                    <td className="px-4 py-4 whitespace-nowrap align-top text-center text-sm">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full border ${product.es_visible ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30'}`}>{product.es_visible ? t('productsPage.item.badges.yes', 'Sí') : t('productsPage.item.badges.no', 'No')}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap align-top text-center text-sm">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full border ${product.es_comprable ? 'bg-green-500/20 text-green-300 border-green-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30'}`}>{product.es_comprable ? t('productsPage.item.badges.yes', 'Sí') : t('productsPage.item.badges.no', 'No')}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium align-top">
                      <button onClick={() => handleEditClick(product)} className="btn-taratic btn-orange btn-sm rounded-full mr-2" title="Editar">
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button onClick={() => handleDeleteClick(product.id)} className="btn-taratic btn-red btn-sm rounded-full" title="Eliminar">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* --- PAGINATION --- */}
        {pages > 1 && (
          <div className="bg-black/75 border-t border-white/10 px-6 py-4 flex items-center justify-between sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-taratic px-4 py-2 !text-sm !bg-transparent border border-white/10 text-white/70 hover:text-white hover:!bg-white/5 disabled:opacity-50">Anterior</button>
              <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="btn-taratic px-4 py-2 !text-sm !bg-transparent border border-white/10 text-white/70 hover:text-white hover:!bg-white/5 disabled:opacity-50">Siguiente</button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-white/70">
                  Mostrando <span className="font-medium">{(page - 1) * limit + 1}</span> a <span className="font-medium">{Math.min(page * limit, total)}</span> de <span className="font-medium">{total}</span> resultados
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex items-center gap-2" aria-label="Pagination">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-taratic px-3 py-2 !text-sm !bg-transparent border border-white/10 text-white/60 hover:text-white hover:!bg-white/5 disabled:opacity-50">
                    <span className="sr-only">Anterior</span>
                    <FontAwesomeIcon icon={faChevronLeft} className="h-4 w-4" aria-hidden="true" />
                  </button>

                  {[...Array(pages)].map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setPage(i + 1)}
                      className={`btn-taratic px-4 py-2 !text-sm ${page === i + 1 ? 'z-10 !bg-blue-500/20 border border-blue-500/30 text-blue-300' : '!bg-transparent border border-white/10 text-white/60 hover:text-white hover:!bg-white/5'}`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="btn-taratic px-3 py-2 !text-sm !bg-transparent border border-white/10 text-white/60 hover:text-white hover:!bg-white/5 disabled:opacity-50">
                    <span className="sr-only">Siguiente</span>
                    <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4" aria-hidden="true" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductsPage;
